import React from 'react';
import { Card, Table, Button, Modal, Form, Input, message, Select, Space, Tag, Checkbox, Row, Col, Divider, Tabs } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SaveOutlined, TeamOutlined, DollarOutlined } from '@ant-design/icons';

import { useAuthStore } from '../store/authStore';
import SalaryCalculation from './SalaryCalculation';

interface TeamMember {
  id: number;
  email: string;
  username: string;
  fullName: string;
  isActive: boolean;
  createdAt: string;
  permissions: string[];
  earnings: number;
}

const AVAILABLE_PERMISSIONS = [
  { key: 'view_tickets', label: 'Просмотр обращений' },
  { key: 'reply_tickets', label: 'Ответ на обращения' },
  { key: 'close_tickets', label: 'Закрытие обращений' },
  { key: 'view_reports', label: 'Просмотр отчетов' },
  { key: 'manage_team', label: 'Управление командой' },
  { key: 'manage_permissions', label: 'Управление правами' }
];

const SupportTeam: React.FC = () => {
  const [loading, setLoading] = React.useState(false);
  const [members, setMembers] = React.useState<TeamMember[]>([]);
  const [searchText, setSearchText] = React.useState('');
  const [modalVisible, setModalVisible] = React.useState(false);
  const [editingMember, setEditingMember] = React.useState<TeamMember | null>(null);
  const [form] = Form.useForm();
  const [permissionsVisible, setPermissionsVisible] = React.useState(false);
  const [selectedMember, setSelectedMember] = React.useState<TeamMember | null>(null);
  const [selectedPermissions, setSelectedPermissions] = React.useState<string[]>([]);
  const { user: currentUser } = useAuthStore();

  const defaultMembers: TeamMember[] = [
    {
      id: 1,
      email: 'support1@example.com',
      username: 'support_admin',
      fullName: 'Иван Иванов',
      isActive: true,
      createdAt: new Date().toISOString(),
      permissions: ['view_tickets', 'reply_tickets', 'close_tickets', 'view_reports', 'manage_team', 'manage_permissions'],
      earnings: 120000
    },
    {
      id: 2,
      email: 'support2@example.com',
      username: 'support_user1',
      fullName: 'Мария Петрова',
      isActive: true,
      createdAt: new Date().toISOString(),
      permissions: ['view_tickets', 'reply_tickets', 'close_tickets', 'view_reports'],
      earnings: 80000
    },
    {
      id: 3,
      email: 'support3@example.com',
      username: 'support_user2',
      fullName: 'Петр Сидоров',
      isActive: true,
      createdAt: new Date().toISOString(),
      permissions: ['view_tickets', 'reply_tickets'],
      earnings: 50000
    }
  ];

  const load = async () => {
    setLoading(true);
    try {
      const stored = localStorage.getItem('lunch_support_team');
      if (stored) {
        setMembers(JSON.parse(stored));
      } else {
        setMembers(defaultMembers);
        localStorage.setItem('lunch_support_team', JSON.stringify(defaultMembers));
      }
    } catch (e: any) {
      message.error('Ошибка загрузки команды');
      setMembers(defaultMembers);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => { load(); }, []);

  const handleCreateOrUpdate = async () => {
    try {
      const values = await form.validateFields();
      
      if (editingMember) {
        const updated = members.map(m =>
          m.id === editingMember.id
            ? { ...m, ...values, permissions: editingMember.permissions }
            : m
        );
        setMembers(updated);
        localStorage.setItem('lunch_support_team', JSON.stringify(updated));
        message.success('Сотрудник обновлен');
      } else {
        const newMember: TeamMember = {
          id: Math.max(...members.map(m => m.id), 0) + 1,
          ...values,
          createdAt: new Date().toISOString(),
          permissions: ['view_tickets', 'reply_tickets'],
          earnings: 0 // Default earnings for new members
        };
        const updated = [...members, newMember];
        setMembers(updated);
        localStorage.setItem('lunch_support_team', JSON.stringify(updated));
        message.success('Сотрудник добавлен');
      }
      setModalVisible(false);
      setEditingMember(null);
      form.resetFields();
    } catch (e: any) {
      message.error('Ошибка сохранения');
    }
  };

  const handleDelete = (member: TeamMember) => {
    Modal.confirm({
      title: 'Удалить сотрудника?',
      content: `Вы уверены, что хотите удалить ${member.fullName}?`,
      okText: 'Удалить',
      cancelText: 'Отмена',
      okButtonProps: { danger: true },
      onOk: () => {
        const updated = members.filter(m => m.id !== member.id);
        setMembers(updated);
        localStorage.setItem('lunch_support_team', JSON.stringify(updated));
        message.success('Сотрудник удален');
      }
    });
  };

  const handleOpenPermissions = (member: TeamMember) => {
    setSelectedMember(member);
    setSelectedPermissions(member.permissions);
    setPermissionsVisible(true);
  };

  const handleSavePermissions = () => {
    if (!selectedMember) return;
    
    const updated = members.map(m =>
      m.id === selectedMember.id
        ? { ...m, permissions: selectedPermissions }
        : m
    );
    setMembers(updated);
    localStorage.setItem('lunch_support_team', JSON.stringify(updated));
    message.success('Права обновлены');
    setPermissionsVisible(false);
  };

  const openEdit = (member: TeamMember) => {
    setEditingMember(member);
    form.setFieldsValue(member);
    setModalVisible(true);
  };

  const openCreate = () => {
    setEditingMember(null);
    form.resetFields();
    setModalVisible(true);
  };

  const filteredMembers = members.filter(m =>
    m.username.toLowerCase().includes(searchText.toLowerCase()) ||
    m.email.toLowerCase().includes(searchText.toLowerCase()) ||
    m.fullName.toLowerCase().includes(searchText.toLowerCase())
  );

  const getPermissionLabel = (key: string) => {
    return AVAILABLE_PERMISSIONS.find(p => p.key === key)?.label || key;
  };

  const columns = [
    {
      title: 'ФИО',
      dataIndex: 'fullName',
      key: 'fullName',
      width: 150,
      render: (name: string) => (
        <span style={{ fontSize: '13px', fontWeight: 500 }}>{name}</span>
      ),
      sorter: (a: TeamMember, b: TeamMember) => a.fullName.localeCompare(b.fullName)
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 180,
      render: (email: string) => (
        <span style={{ fontSize: '13px' }}>{email}</span>
      )
    },
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
      width: 130,
      render: (username: string) => (
        <span style={{ fontSize: '13px', fontFamily: 'monospace', color: '#666' }}>{username}</span>
      )
    },
    {
      title: 'Права доступа',
      dataIndex: 'permissions',
      key: 'permissions',
      width: 200,
      render: (perms: string[]) => (
        <span style={{ fontSize: '12px', color: '#666' }}>
          {perms.length} прав ({perms.length}/{AVAILABLE_PERMISSIONS.length})
        </span>
      )
    },
    {
      title: 'Заработок',
      dataIndex: 'earnings',
      key: 'earnings',
      width: 120,
      render: (earnings: number) => (
        <span style={{ fontSize: '13px', fontWeight: 500 }}>{earnings.toLocaleString()} ₽</span>
      ),
      sorter: (a: TeamMember, b: TeamMember) => a.earnings - b.earnings
    },
    {
      title: 'Статус',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Активен' : 'Неактивен'}
        </Tag>
      )
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 200,
      fixed: 'right' as const,
      render: (_: any, record: TeamMember) => (
        <Space size="small" wrap>
          <Button
            size="small"
            type="primary"
            ghost
            onClick={() => handleOpenPermissions(record)}
            style={{ fontSize: '12px' }}
          >
            Права
          </Button>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEdit(record)}
            style={{ fontSize: '12px' }}
          >
            Редактировать
          </Button>
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          />
        </Space>
      )
    }
  ];

  return (
    <Tabs
      defaultActiveKey="team"
      items={[
        {
          key: 'team',
          label: (
            <span>
              <TeamOutlined />
              Команда поддержки
            </span>
          ),
          children: (
            <Card
              title="Команда поддержки"
              loading={loading}
              extra={
                <Space>
                  <Input
                    placeholder="Поиск по ФИО, email, username"
                    style={{ width: 250 }}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={openCreate}
                  >
                    Добавить сотрудника
                  </Button>
                </Space>
              }
            >
        <Table
          rowKey="id"
          columns={columns as any}
          dataSource={filteredMembers}
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: 'Сотрудники не найдены' }}
        />

        {/* Модал создания/редактирования */}
        <Modal
          title={editingMember ? `Редактировать: ${editingMember.fullName}` : 'Добавить сотрудника'}
          open={modalVisible}
          onCancel={() => {
            setModalVisible(false);
            setEditingMember(null);
            form.resetFields();
          }}
          onOk={handleCreateOrUpdate}
          okText={editingMember ? 'Сохранить' : 'Добавить'}
          width={600}
          styles={{ body: { padding: '20px' } }}
        >
          <Form form={form} layout="vertical">
            <Form.Item
              label="ФИО"
              name="fullName"
              rules={[{ required: true, message: 'Введите ФИО' }]}
            >
              <Input placeholder="Полное имя" size="large" />
            </Form.Item>
            <Form.Item
              label="Email"
              name="email"
              rules={[
                { required: true, message: 'Введите email' },
                { type: 'email', message: 'Некорректный email' }
              ]}
            >
              <Input placeholder="email@example.com" size="large" />
            </Form.Item>
            <Form.Item
              label="Username"
              name="username"
              rules={[{ required: true, message: 'Введите username' }]}
            >
              <Input placeholder="support_user" size="large" style={{ fontFamily: 'monospace' }} />
            </Form.Item>
            <Form.Item
              label="Статус"
              name="isActive"
              valuePropName="checked"
            >
              <Checkbox>Активный</Checkbox>
            </Form.Item>
            <Form.Item
              label="Заработок"
              name="earnings"
              rules={[{ type: 'number', min: 0, message: 'Заработок должен быть неотрицательным числом' }]}
            >
              <Input
                type="number"
                placeholder="0"
                size="large"
                addonAfter="₽"
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Form>
        </Modal>

        {/* Модал управления правами */}
        <Modal
          title={`Управление правами: ${selectedMember?.fullName}`}
          open={permissionsVisible}
          onCancel={() => setPermissionsVisible(false)}
          onOk={handleSavePermissions}
          okText="Сохранить"
          width={600}
          styles={{ body: { padding: '20px' } }}
        >
          <div>
            <div style={{ marginBottom: '16px', fontSize: '13px', color: '#666' }}>
              Выберите права доступа для этого сотрудника:
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {AVAILABLE_PERMISSIONS.map(perm => (
                <Checkbox
                  key={perm.key}
                  checked={selectedPermissions.includes(perm.key)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedPermissions([...selectedPermissions, perm.key]);
                    } else {
                      setSelectedPermissions(selectedPermissions.filter(p => p !== perm.key));
                    }
                  }}
                >
                  <span style={{ fontSize: '13px' }}>{perm.label}</span>
                </Checkbox>
              ))}
            </div>
            <Divider />
            <div style={{ fontSize: '12px', color: '#999' }}>
              Выбрано прав: {selectedPermissions.length}/{AVAILABLE_PERMISSIONS.length}
            </div>
          </div>
        </Modal>
            </Card>
          )
        },
        {
          key: 'salary',
          label: (
            <span>
              <DollarOutlined />
              Расчет зарплаты
            </span>
          ),
          children: <SalaryCalculation />
        }
      ]}
    />
  );
};

export default SupportTeam;
